import React from "react";
import { Table, Pagination } from "@theorgchartdev/storybook";
import { getImagePath } from "@coreapp/utils/getImagePath";
import {
  FormatValueFromFieldName,
  GetFieldDefsFromList,
  GetPhotoFromDataValue
} from "@/Utils/DataLib";
import * as StateLib from "@/Utils/StateLib";
import { getLocalizedTableHeaderStrings, getLocalizedPaginationStrings } from "@/Utils/Utils";
import { enumFieldType, kNameID } from "@coreapp/constants";
import { isBlockedField, getBlockedFieldDisplayValue } from "@coreapp/utils/FormatFieldValue";
import { getBase64Image } from "@coreapp/utils/getBase64Image";

const TableResultsModal = ({
  tableColumnNames,
  tableHandleRef,
  tableData,
  onTableRowSelection,
  startPage,
  dataTestIdPrefix,
  caption
}) => {
  const [filteredData, setFilteredData] = React.useState([]);
  const [paginatedData, setPaginatedData] = React.useState([]);
  const [selectedRecord, setSelectedRecord] = React.useState(null);

  const searchColumnDefs = GetFieldDefsFromList(tableColumnNames);

  const handleDataChange = newData => {
    setPaginatedData(newData);
  };

  const columnGetValue = (recordData, fieldName) => {
    return FormatValueFromFieldName(fieldName, recordData[fieldName]);
  };

  const commonColumnOptions = {
    getValue: (recordData, fieldName) => {
      return columnGetValue(recordData, fieldName);
    },
    getFilterValue: (recordData, fieldName) => {
      if (isBlockedField(recordData[fieldName])) return getBlockedFieldDisplayValue();
      return columnGetValue(recordData, fieldName) ?? "";
    }
  };

  const nameFieldColumnOptions = {
    getColumnStyle: (header, colIdx) => {
      return { fontWeight: "700", fontSize: "14px" };
    },
    ...commonColumnOptions
  };

  const handleRowClick = (event, data) => {
    // Keyboard activation carries no click count; Enter/Space opens the record like a double click
    if (event.type === "keydown") {
      handleRecordClick(data);
      if (onTableRowSelection) onTableRowSelection(data);
      return;
    }
    switch (event.detail) {
      // single click file handling
      case 1:
        handleRecordClick(data);
        break;
      case 2:
        // double click file handling
        if (onTableRowSelection) onTableRowSelection(data);
        break;
      default:
    }
  };

  const handleRecordClick = data => {
    setSelectedRecord(data);
  };

  const gridOptions = {
    // set background colour on every row, this is probably bad, should be using CSS classes
    // set background colour on even rows again, this looks bad, should be using CSS classes
    getRowStyle: (data, rowIndex, isHovered) => {
      if (selectedRecord && data === selectedRecord)
        return {
          background: "#fcf5ed",
          color: "#ed9e49",
          fontWeight: "500",
          cursor: "pointer",
          border: "#ed9e49"
        };
      else if (isHovered) {
        return { background: "#ececed", cursor: "pointer" };
      }
    },
    onRowClick: handleRowClick
  };

  const GetDirectoryImg = (data, photoValue) => {
    let photoSrc = getImagePath("/static/images/OC-logo.png");

    if (photoValue) {
      const doc = StateLib.GetDoc();
      const callBack = {
        ImageLoaded: pRef => {
          if (pRef) {
            const pDir = pRef.fDirectory;
            const pFile = pRef.fFileName;
            const img = doc.FindPicture(pDir, pFile);
            photoSrc = img?.canvasImgUrl;
          }
        },
        canvasBufferCache: true,
        SubscriberImageLoaded: () => {}
      };
      if (doc) {
        const objDB = doc.GetObjDB();
        const bClass = objDB.GetClassByName("Box");
        const photoFieldDef = bClass.GetFieldDefByID(photoValue);
        const image = GetPhotoFromDataValue(photoFieldDef, data[photoValue], callBack);
        if (image != null) {
          if (!image?.canvasImgUrl) image.canvasImgUrl = getBase64Image(image);
          photoSrc = image?.canvasImgUrl;
        }
      }
    }
    return photoSrc;
  };

  let photoFieldColumnOptions = {
    imgClass: "",
    getPicSource: GetDirectoryImg
  };

  const GetColumnOptions = fieldID => {
    switch (fieldID) {
      case "Name":
        return nameFieldColumnOptions;
      case "Photo":
        return photoFieldColumnOptions;
      default:
        return commonColumnOptions;
    }
  };

  const BuildHeaderData = () => {
    let headers = [];

    for (const columnDef of searchColumnDefs) {
      let headerRow = {};
      const fieldID = columnDef.GetFieldID();
      headerRow.field = columnDef.GetFieldName();
      headerRow.columnOptions = GetColumnOptions(fieldID);
      headerRow.headerName = columnDef.GetFieldLabel();
      headerRow.type = columnDef.GetFieldType() === enumFieldType.image ? "photoCol" : "defaultCol";
      headers.push(headerRow);
    }

    const photoColumn = headers.find(header => header.type === "photoCol");
    if (photoColumn) {
      const nameColumn = headers.find(header => header.field === "Name");

      let headersWithPhoto = headers.filter(
        header => header.field !== "Photo" && header.field !== "Name"
      );

      if (nameColumn) {
        nameColumn.initSortDirection = "asc";
        headersWithPhoto.unshift(nameColumn);
      }

      headersWithPhoto.unshift(photoColumn);

      return headersWithPhoto;
    }

    const nameFieldName = StateLib.GetDoc()
      ?.GetBoxClass()
      ?.GetFieldDefByID(kNameID)
      ?.GetFieldName();
    const nameColumn = headers.find(header => header.field === nameFieldName);

    if (nameColumn) {
      nameColumn.initSortDirection = "asc";
    }

    return headers;
  };

  let availableHeight = StateLib.GetModalContainerHeight();
  let sizeProps = {
    height: availableHeight + "px"
  };

  return (
    <React.Fragment>
      <div className="directory-container" style={sizeProps}>
        <div
          className="directory-table ocn-list-scrollbar"
          style={{
            maxHeight: sizeProps?.height,
            maxWidth: sizeProps?.width
          }}
        >
          <Table
            columns={BuildHeaderData()}
            data={tableData}
            showHeader={true}
            rowSize="largeR"
            textSize="mediumT"
            containerPadding={true}
            gridOptions={gridOptions}
            headerControlProps={{
              textTags: getLocalizedTableHeaderStrings()
            }}
            sortEnabled={true}
            filterEnabled={true}
            onFilterChange={setFilteredData}
            paginatedIndices={paginatedData}
            handleRef={tableHandleRef}
            dataTestId={dataTestIdPrefix}
            caption={caption}
          />
        </div>
        <div className="directory-pagination">
          <Pagination
            dataTestIdPrefix={dataTestIdPrefix}
            modifier="full"
            data={filteredData}
            onDataChange={handleDataChange}
            customStyle={{ padding: "12px 24px" }}
            startPage={startPage}
            textTags={getLocalizedPaginationStrings()}
            preserveSelectedPage
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default TableResultsModal;
